import { CivilStatus } from '@idc-forms/types/civil-status'
import { Gender } from '@idc-forms/types/gender'
import { MigrationStatus } from '@idc-forms/types/migration-status'
import { z } from 'zod'
import { AddressSchema } from '../../domains/address'
import { IdentificationCardSchema } from '../../domains/identification'
import { LastNameSchema } from '../../domains/last-name'
import { NameSchema } from '../../domains/name'
import { PlaceSchema } from '../../domains/place'
import { email, nitOptional, optionalString, requiredString } from '../../primitives'
import { MESSAGES } from '../../utils/messages'
import { PartnerPepSchema, PersonPepSchema, RelativePepSchema } from './pep'

/**
 * Representante legal / apoderado (representación VOLUNTARIA: poder/mandato).
 *
 * En KYC/IVE el expediente identifica al beneficiario final (TITULAR, en
 * `applicant`) Y al representante que actúa por él. El switch es `applicant.ownAct`:
 * cuando es `false` el titular NO actúa en nombre propio, hay representante(s) y esta
 * sección se exige. NO hay un flag propio (`hasLegalRepresentatives` sería un segundo
 * booleano redundante con `ownAct` → fuente única, sin desincronización).
 *
 * Cada representante reusa la forma de identificación del applicant (espejo del modelo
 * interno `features/clients/models/legal-representative.ts`) + el nexo del poder:
 * `qualityRepresentative` (calidad con la que actúa) y `powerRegister` (inscripción del
 * mandato en el Registro Electrónico de Poderes: número/folio/libro/registro).
 *
 * Datos de cumplimiento → fuente ÚNICA acá (front + API) y persistencia estructurada en
 * Mongoose (NO Mixed), igual que applicant/PEP.
 */

/** Inscripción del mandato/poder (Registro Electrónico de Poderes). */
export const PowerRegisterSchema = z.object({
  /** Nombre del registro donde quedó inscrito el mandato. */
  name: requiredString(),
  /** Número de inscripción. */
  number: requiredString(),
  /** Folio. */
  sheet: requiredString(),
  /** Libro. */
  book: requiredString()
})

/**
 * Un representante legal. Identificación (espejo del applicant) + nexo del poder.
 * Los campos son requeridos pero sólo se exigen en el submit estricto (el auto-save
 * guarda parciales sin parsear), igual que las entradas PEP.
 */
export const LegalRepresentativeSchema = z.object({
  id: optionalString(),
  /** Calidad con la que actúa el representante (mandatario, tutor, etc.). */
  qualityRepresentative: requiredString(),
  name: NameSchema,
  lastName: LastNameSchema,
  identificationCard: IdentificationCardSchema,
  birthplace: PlaceSchema,
  birthdate: requiredString(),
  nationality: z.array(requiredString()).min(1, MESSAGES.REQUIRED),
  migrationStatus: z.nativeEnum(MigrationStatus).optional(),
  otherMigrationStatus: optionalString(),
  gender: z.nativeEnum(Gender, { required_error: MESSAGES.REQUIRED }),
  civilStatus: z.nativeEnum(CivilStatus, { required_error: MESSAGES.REQUIRED }),
  profession: requiredString(),
  otherProfession: optionalString(),
  nit: nitOptional(),
  economicActivityRTU: optionalString(),
  phone: z.array(requiredString()).min(1, MESSAGES.REQUIRED),
  email: email(),
  residence: AddressSchema,
  powerRegister: PowerRegisterSchema,
  isPep: z.boolean().default(false),
  pep: z.array(PersonPepSchema).default([]),
  hasRelativesPep: z.boolean().default(false),
  relativesPep: z.array(RelativePepSchema).default([]),
  hasPartnersPep: z.boolean().default(false),
  partnersPep: z.array(PartnerPepSchema).default([]),
  isCPE: z.boolean().default(false)
})

/**
 * Variante INDIVIDUAL del representante. El poder/mandato del titular individual
 * (apoderado/mandatario/tutor) NO se inscribe en el Registro Mercantil — eso es propio del
 * nombramiento de la persona JURÍDICA. Por eso el registro (`powerRegister`) NO aplica y NO
 * se exige: sólo se conserva la calidad con la que actúa + la identidad. El alta interna del
 * individual nunca capturó estos campos, así que el portal tampoco los pide.
 */
export const LegalRepresentativeSchemaIndividual = LegalRepresentativeSchema.extend({
  powerRegister: PowerRegisterSchema.partial().optional()
})

/** Notario que autorizó el nombramiento (nombre + género, para redacción de documentos). */
export const AppointmentNotarySchema = z.object({
  name: requiredString(),
  gender: z.nativeEnum(Gender, { required_error: MESSAGES.REQUIRED })
})

/**
 * Variante JURÍDICA del representante. Además del registro del nombramiento (`powerRegister`
 * → `appointment.appointmentRegister`), captura el resto del nombramiento que el registro
 * interno guarda: cargo, plazo (definido/indefinido), vigencia y notario. Así el portal es
 * espejo del alta y el writeback no pierde nada. `finalDate` sólo se exige con plazo definido
 * (ver `LegalRepresentativeDeclaration`). El individual NO tiene estos campos.
 */
export const LegalRepresentativeSchemaJuridical = LegalRepresentativeSchema.extend({
  positionAppointment: requiredString(),
  termAppointment: z.enum(['I', 'D'], { required_error: MESSAGES.REQUIRED }),
  initialDate: requiredString(),
  finalDate: optionalString(),
  notary: AppointmentNotarySchema
})

const baseJuridical = z.object({
  representatives: z.array(LegalRepresentativeSchemaJuridical).default([])
})

const baseIndividual = z.object({
  representatives: z.array(LegalRepresentativeSchemaIndividual).default([])
})

/**
 * Reglas comunes a ambos tipos: al menos un representante y, por entrada, las declaraciones
 * PEP requeridas cuando su bandera está activa. NO toca `powerRegister` (eso lo cubre el
 * schema de cada entrada), así que sirve igual para el estricto jurídico y el individual.
 */
function refineRepresentatives(
  data: {
    representatives?: Array<{
      isPep?: boolean
      pep?: unknown[]
      hasRelativesPep?: boolean
      relativesPep?: unknown[]
      hasPartnersPep?: boolean
      partnersPep?: unknown[]
    }>
  },
  ctx: z.RefinementCtx
): void {
  const representatives = data.representatives ?? []
  if (representatives.length === 0) {
    ctx.addIssue({
      path: ['representatives'],
      code: z.ZodIssueCode.custom,
      message: MESSAGES.REQUIRED
    })
  }
  representatives.forEach((representative, index) => {
    if (representative.isPep && (representative.pep?.length ?? 0) === 0) {
      ctx.addIssue({
        path: ['representatives', index, 'pep'],
        code: z.ZodIssueCode.custom,
        message: MESSAGES.REQUIRED
      })
    }
    if (representative.hasRelativesPep && (representative.relativesPep?.length ?? 0) === 0) {
      ctx.addIssue({
        path: ['representatives', index, 'relativesPep'],
        code: z.ZodIssueCode.custom,
        message: MESSAGES.REQUIRED
      })
    }
    if (representative.hasPartnersPep && (representative.partnersPep?.length ?? 0) === 0) {
      ctx.addIssue({
        path: ['representatives', index, 'partnersPep'],
        code: z.ZodIssueCode.custom,
        message: MESSAGES.REQUIRED
      })
    }
  })
}

/**
 * Schema ESTRICTO (submit) — persona JURÍDICA. Sólo se parsea cuando la sección está
 * habilitada Y `applicant.ownAct === false` (el normalizador del submit limpia el array si el
 * titular actúa en nombre propio). Exige al menos un representante; cada entrada se valida
 * campo a campo por `LegalRepresentativeSchema`, incluido el registro del nombramiento
 * (`powerRegister`). El auto-save NO usa esto.
 */
export const LegalRepresentativeDeclaration = baseJuridical.superRefine((data, ctx) => {
  refineRepresentatives(data, ctx)
  // La fecha final sólo aplica (y se exige) cuando el nombramiento es de plazo DEFINIDO.
  data.representatives.forEach((representative, index) => {
    if (representative.termAppointment === 'D' && !representative.finalDate?.trim()) {
      ctx.addIssue({
        path: ['representatives', index, 'finalDate'],
        code: z.ZodIssueCode.custom,
        message: MESSAGES.REQUIRED
      })
    }
  })
})

/**
 * Schema ESTRICTO (submit) — persona INDIVIDUAL. Igual que el jurídico pero SIN exigir el
 * registro del poder (`powerRegister` opcional) ni los datos del nombramiento (cargo/plazo/
 * vigencia/notario), porque el mandato individual no se inscribe en el Registro Mercantil.
 * Ver `LegalRepresentativeSchemaIndividual`.
 */
export const LegalRepresentativeDeclarationIndividual =
  baseIndividual.superRefine(refineRepresentatives)

export type LegalRepresentative = z.infer<typeof LegalRepresentativeSchema>
export type PowerRegister = z.infer<typeof PowerRegisterSchema>
export type LegalRepresentativeDeclaration = z.infer<typeof baseJuridical>
